import { useRef } from 'react';

import {
  Box,
  Button,
  Flex,
  Overlay,
  Popover,
  Typography,
  useOverlayPosition,
} from '@project44/manifest-ds';
import { useTranslation } from '@project44/translations-utils';

const styles = {
  root: {
    top: 0,
    left: 0,
    alignItems: 'center',
    bgColor: 'rgba(10, 21, 33, 0.37)',
    height: '100%',
    justifyContent: 'center',
    width: '100%',
  },
  modal: {
    bgColor: '$background-primary',
    maxWidth: '600px',
    borderRadius: '$small',
    boxShadow: '$medium',
  },
  content: {
    borderBottom: '1px solid $border-primary',
    padding: '0 $medium $medium',
    color: '$text-secondary',
  },
  buttons: {
    padding: '$medium',
  },
};

interface ConfirmModalProps {
  cancelText?: string;
  confirmText?: string;
  customConfirmButton?: React.ReactNode;
  description: string;
  isOpen?: boolean;
  onCancel?: () => void;
  onConfirm?: () => void;
  title: string;
  titleIcon?: React.ReactNode;
}

function ConfirmModal({
  cancelText,
  confirmText,
  customConfirmButton,
  description,
  isOpen = false,
  onCancel,
  onConfirm,
  title,
  titleIcon,
}: ConfirmModalProps) {
  const { t } = useTranslation();
  const overlayRef = useRef(null);
  const targetRef = useRef(null);
  const { overlayProps } = useOverlayPosition({
    isOpen,
    overlayRef,
    targetRef,
  });

  const cancel = cancelText || t('common.cancel');
  const confirm = confirmText || t('common.confirm');

  return (
    <Overlay isOpen={isOpen}>
      <Popover
        css={styles.root}
        isOpen={isOpen}
        onClick={onCancel}
        ref={overlayRef}
        {...overlayProps}
      >
        <Box css={styles.modal}>
          <Flex align="center" gap="small" css={styles.buttons}>
            {titleIcon}
            <Typography variant="title">{title}</Typography>
          </Flex>
          <Typography variant="body" as="p" css={styles.content}>
            {description}
          </Typography>
          <Flex align="end" justify="end" gap="medium" css={styles.buttons}>
            <Button aria-label={confirm} onPress={onCancel} variant="secondary">
              {cancel}
            </Button>
            {customConfirmButton || (
              <Button aria-label={confirm} onPress={onConfirm}>
                {confirm}
              </Button>
            )}
          </Flex>
        </Box>
      </Popover>
    </Overlay>
  );
}

export default ConfirmModal;
